import type { AuctisyncSupabase } from './client.js';

/**
 * Bidder multi-house support (Run Brief P2.4).
 *
 * One AuctiSync account can be registered with several auction houses. After
 * sign-in the app asks `get_my_registered_tenants` which houses this bidder
 * belongs to, and routes on the COUNT: none, exactly one, or several.
 *
 * WHY THIS LIVES HERE. The web already implements the same rules inside
 * `apps/web/src/hooks/useBidderHouses.tsx`, tangled together with React state,
 * localStorage persistence and an onboarding-preview override. The native app
 * needs the same DECISION without any of that, and "share LOGIC only" (Run
 * Brief D-share) means extracting the rule rather than re-deriving it — two
 * implementations of "which house am I in" would eventually disagree about
 * something a bidder can see.
 *
 * The RPC is SECURITY DEFINER and scoped to `auth.uid()`'s own ACTIVE
 * memberships: the post-auth and own-rows-only guarantees live in the database,
 * not in this file. Calling it while signed out returns nothing rather than
 * everything.
 *
 * FOLLOW-UP (logged, not done here): migrate the web hook onto these functions
 * so there is one implementation. That touches a live signed-in surface, so it
 * is a deliberate staging-first change, not a drive-by.
 */

/** A house the bidder is registered with, as `get_my_registered_tenants` returns it. */
export interface BidderHouse {
  tenant_id: string;
  name: string;
  display_name: string | null;
  branding: Record<string, unknown> | null;
  tenant_bidder_number: number | null;
}

/**
 * What the app should do once the houses are known.
 *
 *  - `none`   → registered with nobody; offer to find/join a house.
 *  - `single` → exactly one; enter it without asking. Making someone "choose"
 *               from a list of one is pure friction.
 *  - `choose` → several; the bidder must pick, and there is no safe default
 *               (guessing puts them in the wrong house's catalogue).
 */
export type HouseRouteKind = 'none' | 'single' | 'choose';

export interface HouseRoute {
  kind: HouseRouteKind;
  /** Set only for `single`, or for `choose` when a prior pick is still valid. */
  selectedId: string | null;
}

/**
 * The routing decision. Pure — no network, no storage, no React.
 *
 * `previousId` is a house the bidder was in last time. It is honoured only if
 * it is still in the list: a remembered house they have since been removed from
 * would otherwise drop them into a catalogue they can no longer bid in.
 */
export function routeForHouses(
  houses: readonly BidderHouse[],
  previousId?: string | null,
): HouseRoute {
  if (houses.length === 0) return { kind: 'none', selectedId: null };

  if (houses.length === 1) {
    // Auto-enter. Note this IGNORES previousId — with one house there is
    // nothing to choose, and a stale id must not win over the only real option.
    return { kind: 'single', selectedId: houses[0]?.tenant_id ?? null };
  }

  const remembered =
    previousId && houses.some((h) => h.tenant_id === previousId) ? previousId : null;
  return { kind: 'choose', selectedId: remembered };
}

export interface ListMyHousesResult {
  houses: BidderHouse[];
  /** Present when the RPC failed. `houses` is empty in that case. */
  error?: string;
}

/**
 * Fetch the signed-in bidder's registered houses.
 *
 * Never throws. A transport or RPC failure comes back as `{ houses: [], error }`
 * so callers render one path — the same contract `placeBid` follows. Throwing
 * here would mean every screen needs a try/catch to avoid a blank crash on a
 * flaky mobile connection.
 */
export async function listMyHouses(supabase: AuctisyncSupabase): Promise<ListMyHousesResult> {
  const { data, error } = await supabase.rpc('get_my_registered_tenants');

  if (error) return { houses: [], error: error.message };
  return { houses: (data ?? []) as unknown as BidderHouse[] };
}

/**
 * One house's PUBLIC identity, by id — the reader a signed-out visitor uses.
 *
 * WHY IT IS NOT A `tenants` SELECT. `public.tenants` is readable only by
 * `authenticated`, and then only for tenants the caller belongs to. So a
 * visitor arriving on a lot from a search result — the single most important
 * arrival on a white-label site — could not be told whose sale they were
 * looking at. The website's fallback was the string "AuctiSync", which is the
 * wrong answer to "whose auction is this?" on a tenant's own site.
 *
 * `get_house_identity` is the narrow public projection behind it: id, slug,
 * name, display_name and `metadata.branding`, for houses that are active AND
 * live. Nothing else — the rest of `metadata` holds non-public keys.
 *
 * Returns the same `BidderHouse` shape as `listMyHouses` so a surface can brand
 * itself from either source without a second code path. `tenant_bidder_number`
 * is null: a guest has no paddle number, and inventing one would be a lie on
 * the surface where it matters most.
 *
 * Never throws. A house we cannot name is a house the page names generically,
 * not a blank screen.
 */
export async function fetchHouseIdentity(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<BidderHouse | null> {
  if (tenantId === '') return null;
  try {
    const { data, error } = await supabase.rpc('get_house_identity', { p_tenant_id: tenantId });
    if (error || data === null || typeof data !== 'object') return null;
    const o = data as Record<string, unknown>;
    const name = typeof o['name'] === 'string' ? o['name'] : null;
    if (name === null) return null;
    const display = o['display_name'];
    const branding = o['branding'];
    return {
      tenant_id: tenantId,
      name,
      display_name: typeof display === 'string' ? display : null,
      branding:
        branding !== null && typeof branding === 'object'
          ? (branding as Record<string, unknown>)
          : null,
      tenant_bidder_number: null,
    };
  } catch {
    return null;
  }
}

/** Display label for a house: the public-facing name when set, else the legal one. */
export function houseLabel(house: BidderHouse): string {
  const display = house.display_name?.trim();
  return display && display.length > 0 ? display : house.name;
}

/**
 * This bidder's approval status at each house they have registered with.
 *
 * SEPARATE from `listMyHouses` on purpose. That RPC answers "which houses do I
 * belong to"; this answers "will they let me bid", and the two are genuinely
 * different questions — a bidder can be registered and still pending.
 *
 * Assuming approval because a membership row exists is the tempting shortcut
 * and the wrong one: it tells someone they can bid, and the refusal arrives
 * from `place_bid` at the moment they try, on a lot that is closing.
 *
 * Readable directly because `tenant_buyers_self_select` confines it to the
 * caller's own rows.
 */
export interface HouseMembership {
  tenant_id: string;
  status: string;
}

export async function fetchMyHouseMemberships(
  supabase: AuctisyncSupabase,
): Promise<{ memberships: HouseMembership[]; error: string | null }> {
  const { data, error } = await supabase.from('tenant_buyers').select('tenant_id, status');
  if (error) return { memberships: [], error: error.message };
  return { memberships: (data ?? []) as HouseMembership[], error: null };
}

/**
 * Map a raw membership status to what a bidder needs to know.
 *
 * Anything unrecognised becomes `pending` rather than `approved`. New statuses
 * will appear as the product grows, and the safe direction for an unknown one
 * is "we are not sure you can bid yet" — never "go ahead".
 */
export function approvalOf(status: string | null | undefined): 'approved' | 'pending' | 'rejected' {
  if (status === 'active' || status === 'approved') return 'approved';
  if (status === 'rejected' || status === 'banned') return 'rejected';
  return 'pending';
}
